// React
import React, { useEffect, useState, useCallback, startTransition, useMemo, useRef} from 'react';

// Routing
import { useNavigate } from 'react-router-dom';

// MUI Components
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Autocomplete, Pagination, IconButton, MenuItem, Radio, RadioGroup, FormControlLabel, Button, Chip } from '@mui/material';

// MUI Icons
import ClearIcon from '@mui/icons-material/Clear';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import AddIcon from '@mui/icons-material/Add';

// i18n
import { useTranslation } from 'react-i18next';

// Custom Hooks / Providers
import { useClinic } from '../../../components/ClinicProvider';

// React Query
import { useQuery } from '@tanstack/react-query';

// Services & Utils
import { formatDateYMD, formatDateDMY } from '../../../utils/dateUtils';
import { fetchClinicalInvoicePatients, ClinicalInvoicePatient } from './invoiceHelper';
import { fetchRegisteredPharmacyPatients, RegisteredPharmacyPatient, RegisteredPharmacyPatientType } from './registeredPharmacyPatients.helper';

// Pharmacy New Patient Modal
import { NewPharmacyPatientModal } from '../../../components/Billing/common/NewPharmacyPatientModal';

// Styles & Components
import { styles } from './InvoicePatientSelector.styles';
import { TABLE_HEADER_ROW_SX } from '../../../utils/typographyStyles';
import NoDataRow from '../../../components/common/NoDataRow';
import TableLoader from '../../../components/common/TableLoader';

// ─── Constants ───────────────────────────────────────────────────────────────

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

/** Discriminator for the two table views */
type ViewListType = 'checkedIn' | 'registeredPharmacy';

/** Status filter for the Registered Pharmacy table */
type RegistrationStatusFilter = '' | 'all' | 'Pharmacy' | 'Registered';

// ─── Interfaces ──────────────────────────────────────────────────────────────

/** Row shape for the Checked-In patient table */
interface PatientRow {
  id: number;
  date: string;
  patient: string;
  contact: string;
  doctor: string;
}

/** Row shape for the Registered Pharmacy Patient table */
interface RegisteredPatientRow {
  id: number;
  date: string;
  type?: RegisteredPharmacyPatientType;
  patient: string;
  contact: string;
}

/** Column descriptor used by both table headers */
interface ColumnDef {
  label: string;
  key: string;
}

// ─── Row transformers ────────────────────────────────────────────────────────

/** Maps a clinical invoice patient API object to a table row */
const transformClinicalPatientToRow = (item: ClinicalInvoicePatient): PatientRow => ({
  id: item.id,
  date: item.appointment_date || '',
  patient: `${item.first_name} ${item.last_name || ''}\n${item.medical_record_no || ''} | ${item.gender__name_label || ''}`.trim(),
  contact: item.mobile_number || '',
  doctor: item.appointment_user && item.appointment_user.length > 0
    ? `${item.appointment_user[0].first_name} ${item.appointment_user[0].last_name || ''}`.trim()
    : '',
});

/** Maps a registered pharmacy patient API object to a table row */
const transformRegisteredPatientToRow = (item: RegisteredPharmacyPatient): RegisteredPatientRow => ({
  id: item.id,
  date: item.created_on ? formatDateDMY(item.created_on) : '',
  type: item.registration_type,
  patient: `${item.patient_name || `${item.first_name} ${item.last_name || ''}`}${item.age != null ? ` / ${item.age}` : ''}\n${item.medical_record_no || ''} | ${item.gender_name || ''}`.trim(),
  contact: item.full_mobile_number || item.mobile_number || '',
});

// ─── Reusable sub-components ─────────────────────────────────────────────────

/** Renders table header cells with proper rounded-corner styles */
const renderHeaderCells = (cols: ColumnDef[]) =>
  cols.map((col, index) => (
    <TableCell
      key={col.key}
      sx={{
        px: 2.5,
        ...(index === 0 ? { borderTopLeftRadius: 12, borderBottomLeftRadius: 12 } : {}),
        ...(index === cols.length - 1 ? { borderTopRightRadius: 12, borderBottomRightRadius: 12 } : {}),
      }}
    >
      {col.label}
    </TableCell>
  ));

/** Badge chip indicating whether a patient is a "Pharmacy Walk-In" or "Registered" */
const TypeBadge: React.FC<{ type?: RegisteredPharmacyPatientType; labels: { walkIn: string; registered: string } }> = ({ type, labels }) => {
  const isWalkIn = type === 'Pharmacy';
  return (
    <Chip
      label={isWalkIn ? labels.walkIn : labels.registered}
      size="small"
      sx={isWalkIn ? styles.pharmacyWalkInBadge : styles.registeredBadge}
    />
  );
};

/** A single row in the Checked-In patients table */
const CheckedInTableRow: React.FC<{ row: PatientRow; onClick: (row: PatientRow) => void; ariaLabel: string }> = ({ row, onClick, ariaLabel }) => (
  <TableRow
    key={row.id}
    hover
    tabIndex={0}
    sx={styles.interactiveRow}
    onClick={() => onClick(row)}
    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onClick(row); }}
    aria-label={ariaLabel}
  >
    <TableCell sx={styles.rowCell}>{row.date ? formatDateDMY(row.date) : '-'}</TableCell>
    <TableCell sx={{ ...styles.rowCell, whiteSpace: 'pre-line' }}>{row.patient}</TableCell>
    <TableCell sx={styles.rowCell}>{row.contact}</TableCell>
    <TableCell sx={styles.rowCell}>{row.doctor}</TableCell>
  </TableRow>
);

/** A single row in the Registered Pharmacy Patients table */
const RegisteredTableRow: React.FC<{
  row: RegisteredPatientRow;
  onClick: (row: RegisteredPatientRow) => void;
  ariaLabel: string;
  badgeLabels: { walkIn: string; registered: string };
}> = ({ row, onClick, ariaLabel, badgeLabels }) => (
  <TableRow
    key={row.id}
    hover
    tabIndex={0}
    sx={styles.interactiveRow}
    onClick={() => onClick(row)}
    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onClick(row); }}
    aria-label={ariaLabel}
  >
    <TableCell sx={styles.rowCell}>{row.date || '-'}</TableCell>
    <TableCell sx={styles.rowCell}><TypeBadge type={row.type} labels={badgeLabels} /></TableCell>
    <TableCell sx={{ ...styles.rowCell, whiteSpace: 'pre-line' }}>{row.patient}</TableCell>
    <TableCell sx={styles.rowCell}>{row.contact}</TableCell>
  </TableRow>
);

// ─── Main Component ──────────────────────────────────────────────────────────

const InvoicePatientSelector = () => {
  const { t: tBilling } = useTranslation('billing_config');
  const { ClinicId } = useClinic();
  const navigate = useNavigate();

  // ── Translated labels (memoised to avoid re-creation every render) ──
  const labels = useMemo(() => ({
    title: tBilling('invoice.patient_selector.title'),
    searchLabel: tBilling('invoice.patient_selector.search_label'),
    newPharmacyPatient: tBilling('invoice.patient_selector.new_pharmacy_patient'),
    viewListBy: tBilling('invoice.patient_selector.view_list_by'),
    checkedIn: tBilling('invoice.patient_selector.view_checked_in'),
    registeredPharmacy: tBilling('invoice.patient_selector.view_registered_pharmacy'),
    loading: tBilling('invoice.patient_selector.loading'),
    noDataCheckedIn: tBilling('invoice.patient_selector.no_data.checked_in'),
    noDataRegistered: tBilling('invoice.patient_selector.no_data.registered'),
    rows: tBilling('invoice.patient_selector.footer.rows'),
    badgeWalkIn: tBilling('invoice.patient_selector.badges.pharmacy_walk_in'),
    badgeRegistered: tBilling('invoice.patient_selector.badges.registered'),
    badgeAll: tBilling('invoice.patient_selector.badges.all'),
  }), [tBilling]);

  /** Column definitions – depend on translated labels */
  const checkedInColumns: ColumnDef[] = useMemo(() => [
    { label: tBilling('invoice.patient_selector.columns.visit_date'), key: 'date' },
    { label: tBilling('invoice.patient_selector.columns.patient'), key: 'patient' },
    { label: tBilling('invoice.patient_selector.columns.contact_number'), key: 'contact' },
    { label: tBilling('invoice.patient_selector.columns.treating_doctor'), key: 'doctor' },
  ], [tBilling]);

  const registeredColumns: ColumnDef[] = useMemo(() => [
    { label: tBilling('invoice.patient_selector.columns.registration_date'), key: 'date' },
    { label: tBilling('invoice.patient_selector.columns.type'), key: 'type' },
    { label: tBilling('invoice.patient_selector.columns.patient'), key: 'patient' },
    { label: tBilling('invoice.patient_selector.columns.contact_number'), key: 'contact' },
  ], [tBilling]);

  const badgeLabels = useMemo(() => ({
    walkIn: labels.badgeWalkIn,
    registered: labels.badgeRegistered,
  }), [labels.badgeWalkIn, labels.badgeRegistered]);

  // ── View type, search & pagination state ──
  const [viewType, setViewType] = useState<ViewListType>('checkedIn');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [openNewPatientModal, setOpenNewPatientModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<RegistrationStatusFilter>('');
  const prevDebouncedSearchRef = useRef(debouncedSearch);

  // Debounce user input
  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(h);
  }, [search]);

  // Handle view type change
  const handleViewTypeChange = useCallback((_: React.ChangeEvent<HTMLInputElement>, value: string) => {
    setViewType(value as ViewListType);
    setSearch('');
    setDebouncedSearch('');
    setPage(1);
    setStatusFilter('');
  }, []);

  // --- Checked-In query ---
  const checkedInEnabled = viewType === 'checkedIn' && !!ClinicId && (debouncedSearch.length === 0 || debouncedSearch.length >= 2);
  const checkedInQuery = useQuery({
    queryKey: ['checkedInPatients', ClinicId, page, debouncedSearch, pageSize],
    enabled: checkedInEnabled,
    staleTime: 30000,
    gcTime: 300000,
    queryFn: () => {
      const offset = pageSize * (page - 1);
      const today = new Date();
      const todayStr = formatDateYMD(today);
      return fetchClinicalInvoicePatients({
        clinic: Number(ClinicId),
        from_date: todayStr,
        to_date: todayStr,
        limit: pageSize,
        offset,
        search: debouncedSearch || undefined,
      }).then(res => ({
        rows: res.objects.map(transformClinicalPatientToRow),
        totalCount: res.meta?.total_count || 0,
      }));
    },
  });

  // --- Registered Pharmacy Patients query ---
  const registeredEnabled = viewType === 'registeredPharmacy' && !!ClinicId && (debouncedSearch.length === 0 || debouncedSearch.length >= 2);
  const registeredQuery = useQuery({
    queryKey: ['registeredPharmacyPatients', ClinicId, page, debouncedSearch, pageSize, statusFilter],
    enabled: registeredEnabled,
    staleTime: 30000,
    gcTime: 300000,
    queryFn: () => {
      const offset = pageSize * (page - 1);
      return fetchRegisteredPharmacyPatients({
        clinic: Number(ClinicId),
        limit: pageSize,
        offset,
        search: debouncedSearch || undefined,
        registration_type: (statusFilter && statusFilter !== 'all') ? statusFilter : undefined,
      }).then(res => ({
        rows: res.objects.map(transformRegisteredPatientToRow),
        totalCount: res.meta?.total_count || 0,
      }));
    },
  });

  // Select active query based on view type
  const activeQuery = viewType === 'checkedIn' ? checkedInQuery : registeredQuery;

  const checkedInRows = useMemo(() => checkedInQuery.data?.rows ?? [], [checkedInQuery.data?.rows]);
  const registeredRows = useMemo(() => registeredQuery.data?.rows ?? [], [registeredQuery.data?.rows]);
  const totalCount = activeQuery.data?.totalCount || 0;

  const searchingInFlight =
    activeQuery.isFetching &&
    prevDebouncedSearchRef.current !== debouncedSearch &&
    debouncedSearch.length >= 2;

  const displayedCheckedInRows = useMemo(() => {
    if (debouncedSearch && checkedInRows.length > 0) {
      const s = debouncedSearch.toLowerCase();
      return checkedInRows.filter(r => r.patient.toLowerCase().includes(s));
    }
    return checkedInRows;
  }, [debouncedSearch, checkedInRows]);

  const displayedRegisteredRows = useMemo(() => {
    if (debouncedSearch && registeredRows.length > 0) {
      const s = debouncedSearch.toLowerCase();
      return registeredRows.filter(r => r.patient.toLowerCase().includes(s));
    }
    return registeredRows;
  }, [debouncedSearch, registeredRows]);

  const currentRowCount = viewType === 'checkedIn' ? checkedInRows.length : registeredRows.length;

  const paginationInfo = useMemo(() => {
    const pageCount = Math.ceil(totalCount / pageSize);
    const safePageCount = Math.max(1, pageCount);
    const currentOffset = pageSize * (page - 1);
    const showingStart = totalCount === 0 ? 0 : currentOffset + 1;
    const showingEnd = totalCount === 0 ? 0 : Math.min(currentOffset + currentRowCount, totalCount);
    return { pageCount, safePageCount, showingStart, showingEnd };
  }, [totalCount, page, currentRowCount, pageSize]);

  // ── Event handlers ──

  const handleSearchChange = useCallback((_: React.SyntheticEvent, value: string) => {
    startTransition(() => {
      setSearch(value);
      setPage(1);
    });
  }, []);

  /** Navigate to the New Invoice form for a given patient */
  const navigateToNewInvoice = useCallback((patientId: number, pharmacyOnly?: boolean) => {
    navigate(`/admin/billing/invoice/new/${patientId}`, {
      state: { showTabs: !pharmacyOnly, pharmacyOnly: !!pharmacyOnly, isNewPatient: !!pharmacyOnly },
    });
  }, [navigate]);

  const handleCheckedInRowClick = useCallback((row: PatientRow) => {
    navigateToNewInvoice(row.id);
  }, [navigateToNewInvoice]);

  const handleRegisteredRowClick = useCallback((row: RegisteredPatientRow) => {
    navigateToNewInvoice(row.id, true);
  }, [navigateToNewInvoice]);

  const getOptionLabel = useCallback((option: string | PatientRow) => {
    if (typeof option === 'string') return option;
    return option.patient ? option.patient.replace(/\n.*/, '') : '';
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearch('');
    setDebouncedSearch('');
    setPage(1);
  }, []);

  const tableLoading = activeQuery.isFetching && prevDebouncedSearchRef.current !== debouncedSearch;

  /** Helper to get a row's display name for aria labels */
  const getRowName = (patient: string) => patient.replace(/\n.*/, '');

  // Determine which rows to show based on the current view
  const isCheckedInView = viewType === 'checkedIn';
  const visibleCheckedInRows = searchingInFlight ? [] : displayedCheckedInRows;
  const visibleRegisteredRows = searchingInFlight ? [] : displayedRegisteredRows;

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={styles.pageRoot}>
      <Box sx={styles.cardRoot}>
        {/* ── Header bar ── */}
        <Box sx={styles.headerBar}>
          <IconButton size="small" sx={{ mr: 2 }} onClick={() => navigate(-1)}>
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
          <Typography variant="h6" sx={styles.title}>
            {labels.title}
          </Typography>

          <Box sx={styles.searchWrap}>
            <Autocomplete
              freeSolo
              disableClearable
              filterOptions={(options) => options}
              options={isCheckedInView ? displayedCheckedInRows : []}
              getOptionLabel={getOptionLabel}
              inputValue={search}
              onInputChange={handleSearchChange}
              onClose={() => {/* prevent Autocomplete from mutating input on blur */}}
              sx={{ width: '100%' }}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={labels.searchLabel}
                  size="small"
                  variant="outlined"
                  slotProps={{
                    inputLabel: { shrink: !!search },
                    input: {
                      'aria-label': tBilling('invoice.patient_selector.aria.search_by_patient'),
                      endAdornment: (
                        <>
                          {search && (
                            <IconButton
                              size="small"
                              aria-label={tBilling('invoice.patient_selector.aria.clear_search')}
                              onClick={handleClearSearch}
                              edge="end"
                              sx={{ p: 0.5, mr: 0.5 }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }
                  }}
                />
              )}
            />
          </Box>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenNewPatientModal(true)}
            sx={styles.newPatientBtn}
          >
            {labels.newPharmacyPatient}
          </Button>
        </Box>

        {/* ── View toggle ── */}
        <Box sx={styles.viewToggleBar}>
          <Typography variant="body2" sx={styles.viewToggleLabel}>{labels.viewListBy}</Typography>
          <RadioGroup
            row
            value={viewType}
            onChange={handleViewTypeChange}
            sx={styles.radioGroup}
          >
            <FormControlLabel
              value="checkedIn"
              control={<Radio size="small" sx={styles.orangeRadio} />}
              label={labels.checkedIn}
              sx={styles.radioLabel}
            />
            <FormControlLabel
              value="registeredPharmacy"
              control={<Radio size="small" sx={styles.orangeRadio} />}
              label={labels.registeredPharmacy}
              sx={styles.radioLabel}
            />
          </RadioGroup>
        </Box>

        {/* ── Table ── */}
        <TableContainer sx={styles.scrollableTableContainer}>
          {tableLoading && <TableLoader minHeight={120} size={32} thickness={3} message={labels.loading} />}
          <Table size="small" stickyHeader aria-label={tBilling('invoice.patient_selector.aria.table_label')} sx={styles.table}>
            {isCheckedInView ? (
              <>
                <TableHead>
                  <TableRow sx={TABLE_HEADER_ROW_SX}>{renderHeaderCells(checkedInColumns)}</TableRow>
                </TableHead>
                <TableBody>
                  {visibleCheckedInRows.length > 0
                    ? visibleCheckedInRows.map(row => (
                      <CheckedInTableRow
                        key={row.id}
                        row={row}
                        onClick={handleCheckedInRowClick}
                        ariaLabel={tBilling('invoice.patient_selector.aria.create_invoice_for', { name: getRowName(row.patient) })}
                      />
                    ))
                    : <NoDataRow message={labels.noDataCheckedIn} colSpan={checkedInColumns.length} />}
                </TableBody>
              </>
            ) : (
              <>
                <TableHead>
                  <TableRow sx={TABLE_HEADER_ROW_SX}>{renderHeaderCells(registeredColumns)}</TableRow>
                </TableHead>
                <TableBody>
                  {visibleRegisteredRows.length > 0
                    ? visibleRegisteredRows.map(row => (
                      <RegisteredTableRow
                        key={row.id}
                        row={row}
                        onClick={handleRegisteredRowClick}
                        ariaLabel={tBilling('invoice.patient_selector.aria.create_invoice_for', { name: getRowName(row.patient) })}
                        badgeLabels={badgeLabels}
                      />
                    ))
                    : <NoDataRow message={labels.noDataRegistered} colSpan={registeredColumns.length} />}
                </TableBody>
              </>
            )}
          </Table>
        </TableContainer>

        {/* ── Footer ── */}
        <Box sx={styles.footerWrap}>
          {/* Row 1: Status chips (left) + Rows/Pagination (right) on the SAME line */}
          <Box sx={styles.footerRow}>
            <Box sx={styles.footerLeft}>
              {!isCheckedInView && (
                <Box sx={styles.statusFilterBar}>
                  {(['all', 'Pharmacy', 'Registered'] as RegistrationStatusFilter[]).map((filter) => (
                    <Chip
                      key={filter}
                      label={
                        filter === 'all'
                          ? labels.badgeAll
                          : filter === 'Pharmacy'
                            ? labels.badgeWalkIn
                            : labels.badgeRegistered
                      }
                      variant={statusFilter === filter ? 'filled' : 'outlined'}
                      onClick={() => {
                        setStatusFilter(filter);
                        setPage(1);
                      }}
                      sx={
                        filter === 'all'
                          ? styles.statusChipAll
                          : filter === 'Pharmacy'
                            ? styles.statusChipPharmacy
                            : styles.statusChipRegistered
                      }
                    />
                  ))}
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                select
                size="small"
                label={labels.rows}
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                sx={{ width: 92 }}
                aria-label={tBilling('invoice.patient_selector.aria.rows_per_page')}
              >
                {PAGE_SIZE_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </TextField>
              <Pagination
                count={paginationInfo.safePageCount}
                page={page}
                onChange={(_, p) => setPage(p)}
                shape="rounded"
                color="primary"
                size="small"
                disabled={paginationInfo.pageCount <= 1}
                sx={styles.pagination}
              />
            </Box>
          </Box>
          {/* Row 2: Showing entries text */}
          <Typography variant="body2" sx={styles.footerText}>
            {tBilling('invoice.patient_selector.footer.showing_entries', {
              start: paginationInfo.showingStart,
              end: paginationInfo.showingEnd,
              total: totalCount,
            })}
          </Typography>
        </Box>
      </Box>

      {/* ── New Pharmacy Patient Modal ── */}
      <NewPharmacyPatientModal
        open={openNewPatientModal}
        onClose={() => setOpenNewPatientModal(false)}
        clinicId={ClinicId ?? ''}
        onPatientSelected={(patientId, registrationType) =>
          navigateToNewInvoice(patientId, registrationType !== 'existing')
        }
      />
    </Box>
  );
};

export default InvoicePatientSelector;
